import React from 'react';
import { TableCell, TableRow } from '@mui/material';
import { formatDateTime12Hour } from 'shared/utils/dateUtils';
import EmployeeWidgetCard from './EmployeeWidgetCard';
import {
	formatChecklistDateTimeFromAPI,
	getChecklistAssignedDisplay,
	getChecklistOwnerDisplay,
} from '../utils/checklistWidgetDisplay';
import PastDueDueDate, { dueDateTableCellSx } from '../components/PastDueDueDate';
import { isChecklistPastDue } from '../utils/pastDueDisplay';

export interface EmployeeChecklistItem {
	id: number;
	checklistTemplate?: {
		id?: number;
		label?: string;
		type?: string;
		version?: number;
		signoffRequired?: boolean;
		category?: { label?: string };
		frequency?: { label?: string };
		tasks?: Array<{
			id: number;
			section: string;
			duty: string;
			instructions: string;
			answerType: string;
			expectedAnswer: string;
			listOrder: number;
		}>;
	};
	workplace?: { name?: string };
	organization?: { name?: string } | null;
	scheduledDate: string;
	scheduledDateFormatted?: string;
	scheduledTimeFormatted?: string;
	scheduledTime?: string;
	status?: string;
	checklistSchedule?: {
		assignmentType?: string;
		assignee?: { id?: number; firstName?: string; lastName?: string } | null;
		group?: { id?: number; label?: string } | null;
	};
}

interface EmployeeChecklistWidgetProps {
	loading: boolean;
	items: EmployeeChecklistItem[];
	onItemClick: (item: EmployeeChecklistItem) => void;
}

const EmployeeChecklistWidget = ({
	loading,
	items,
	onItemClick,
}: EmployeeChecklistWidgetProps) => (
	<EmployeeWidgetCard
		title="My Checklist"
		loading={loading}
		colSpan={4}
		emptyMessage="No outstanding checklists"
		isEmpty={items.length === 0}
		header={
			<TableRow>
				<TableCell>Checklist Name</TableCell>
				<TableCell>Assigned</TableCell>
				<TableCell>Owner</TableCell>
				<TableCell sx={dueDateTableCellSx}>Checklist Due</TableCell>
			</TableRow>
		}
	>
		{items.map((item) => (
			<TableRow
				key={item.id}
				className="employee-widget-clickable-row"
				onClick={() => onItemClick(item)}
				sx={{ cursor: 'pointer' }}
			>
				<TableCell>
					<span className="employee-widget-link">
						{item.checklistTemplate?.label || 'Unknown Template'}
					</span>
				</TableCell>
				<TableCell>{getChecklistAssignedDisplay(item)}</TableCell>
				<TableCell>{getChecklistOwnerDisplay(item)}</TableCell>
				<TableCell sx={dueDateTableCellSx}>
					<PastDueDueDate
						formattedDate={
							item.scheduledDateFormatted && item.scheduledTimeFormatted
								? formatChecklistDateTimeFromAPI(
										item.scheduledDateFormatted,
										item.scheduledTimeFormatted,
									)
								: formatDateTime12Hour(item.scheduledDate, item.scheduledTime)
						}
						isPastDue={isChecklistPastDue(item)}
					/>
				</TableCell>
			</TableRow>
		))}
	</EmployeeWidgetCard>
);

export default EmployeeChecklistWidget;
